import useImageLoader from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import Skeleton from '@/common/components/skeleton/Skeleton';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { CHAT_GPT_ROLES } from '../../constants/chatRoles.constants';
import { CHAT_MEDIA_RETURN_TYPE } from '../../constants/chatRequests.constants';
import MessageActions from '../messageActions/MessageActions';
import ChatVideoPlayer from '../chatMedia/ChatVideoPlayer';
import {
	ChatMediaFrame,
	ChatMediaImage,
	ChatMediaMessageWrap,
	ChatMediaSkeleton,
} from '../chatMedia/chatMedia.styles';
import { MessageBubble, MessageRow } from './message.styles';

const Message = ({ msg, onImageClick, person }) => {
	const t = useLanguageData('chat');
	const isSender = msg?.role === CHAT_GPT_ROLES.USER;
	const isAssistant = msg?.role === CHAT_GPT_ROLES.ASSISTANT;
	const isImage = msg?.response_format === CHAT_MEDIA_RETURN_TYPE.IMAGE;
	const isVideo =
		msg?.response_format === CHAT_MEDIA_RETURN_TYPE.VIDEO || Boolean(msg?.video);

	const imageMediaId = isImage ? msg?.image?._id : null;
	const videoMediaId = isVideo ? msg?.video?._id ?? msg?.image?._id : null;
	const { imageSrc: imageMediaSrc, isLoading: isImageLoading } = useImageLoader(imageMediaId);
	const { videoSrc: videoMediaSrc, isLoading: isVideoLoading, hasError: hasVideoError } =
		useVideoLoader(videoMediaId);
	const isMedia = isImage || isVideo;
	const speakableText = msg.content?.trim() || '';
	const messageKey = msg?._id || msg?.id || `${msg?.created_at}-${speakableText.slice(0, 24)}`;

	return (
		<MessageRow $isSender={isSender}>
			{isImage && (
				<ChatMediaMessageWrap>
					<ChatMediaFrame
						onClick={onImageClick}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onImageClick?.();
							}
						}}
						role="button"
						tabIndex={0}
					>
						{(isImageLoading || !imageMediaSrc) && (
							<ChatMediaSkeleton aria-hidden="true">
								<Skeleton
									width="100%"
									height="100%"
									radius="0"
									minWidth="100%"
									minHeight="100%"
								/>
							</ChatMediaSkeleton>
						)}
						{imageMediaSrc ? (
							<ChatMediaImage
								src={imageMediaSrc}
								alt={t.generatedImageAlt}
								$isLoading={isImageLoading}
							/>
						) : null}
					</ChatMediaFrame>
				</ChatMediaMessageWrap>
			)}
			{isVideo && (
				<ChatMediaMessageWrap>
					<ChatMediaFrame>
						<ChatVideoPlayer
							src={videoMediaSrc}
							isLoading={isVideoLoading || !videoMediaSrc}
							hasError={hasVideoError}
							errorText={t.videoLoadError}
						/>
					</ChatMediaFrame>
				</ChatMediaMessageWrap>
			)}
			{!isMedia && (
				<MessageBubble isSender={isSender}>{msg.content}</MessageBubble>
			)}
			{isAssistant && (
				<MessageActions
					person={person}
					text={speakableText}
					messageKey={messageKey}
				/>
			)}
		</MessageRow>
	);
};

export default Message;
